import {
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { Box, Button, Heading, Paragraph } from '@alto-avios/alto-ui';
import Cookie from 'js-cookie';
import TenantLogo from '@/utils/tenantLogo';
import { getCustomEventUrl } from '@/utils/dataLayer';
import { useTranslation } from '@/utils/useTranslation';
import { useGlobalContext } from '@/components/GlobalProvider';
import { Programmes } from '@/utils/constants';

type SuccessBodyText = string[];

const ACCOUNT_COOKIE_VALUE_BY_PROGRAMME: Record<Programmes, string> = {
  [Programmes.BAEC]: 'BA',
  [Programmes.IBP]: 'IB',
  [Programmes.AERC]: 'EI',
};

type SuccessBodyTextConfig = {
  paragraphs: SuccessBodyText;
};

export const Success = () => {
  const { t, tRich } = useTranslation('success');
  const { programmeId } = useGlobalContext();
  const [isNavigating, setIsNavigating] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    globalThis.dataLayer = globalThis.dataLayer || [];
    globalThis.dataLayer.push({
      event: 'enrolment_success',
      page_location: `${getCustomEventUrl(programmeId)}/success`,
    });
  }, [programmeId]);

  const bodyText = t<SuccessBodyTextConfig>('bodyText', {
    returnObjects: true,
  });

  const bodyTextLinks = {
    oneworld: (chunks: ReactNode) => (
      <a
        key="oneworld"
        href="https://www.oneworld.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </a>
    ),
    avios: (chunks: ReactNode) => (
      <a
        key="avios"
        href="https://www.avios.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </a>
    ),
  };

  const handleRedirectToAccount = useCallback(async () => {
    setIsNavigating(true);

    try {
      if (!programmeId) {
        throw new Error('A programme is required to redirect to an account');
      }

      const cookieValue = ACCOUNT_COOKIE_VALUE_BY_PROGRAMME[programmeId];

      Cookie.set('AVIOS_OPCO', cookieValue, {
        sameSite: 'Lax',
        expires: 30, // 30 days
        path: '/',
        secure: process.env.NEXT_PUBLIC_ENVIRONMENT === 'prd',
      });

      globalThis.location.href = 'https://www.avios.com/manage-avios/activity';
    } finally {
      if (isMountedRef.current) {
        setIsNavigating(false);
      }
    }
  }, [programmeId]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      backgroundColor="layer1"
      paddingX="lg"
      paddingY="3xl"
    >
      <Box
        display="flex"
        flexDirection="column"
        gap="lg"
        padding="2xl"
        backgroundColor="base"
        borderColour="secondary"
        borderRadius="md"
        borderStyle="solid"
        borderWidth="xs"
        maxWidth="393px"
      >
        <Box alignItems="center" justifyContent="center">
          <TenantLogo programmeId={programmeId} />
        </Box>

        <Heading size="xs" textAlign="start" data-testid="success-title">
          {t('title')}
        </Heading>

        {bodyText.paragraphs.map(paragraph => (
          <Paragraph key={paragraph}>
            {tRich(`bodyText.${paragraph}`, bodyTextLinks)}
          </Paragraph>
        ))}

        <Button
          fullWidth
          size="md"
          data-testid="success-home-button"
          isDisabled={isNavigating}
          isLoading={isNavigating}
          onPress={handleRedirectToAccount}
        >
          {t('button')}
        </Button>
      </Box>
    </Box>
  );
};
